import { client } from './api';

/**
 * The order faxes a stage with no fax line stored instead of sending, and
 * signing one back as the physician would.
 *
 * Strawhat only, and the whole endpoint answers 404 wherever real faxing is
 * live, so a caller cannot even learn that these exist on a stage that has a
 * telephone. Nothing listed here ever left the building.
 *
 * Every call passes `throwOnError` because the generated client resolves
 * rather than rejects on a 4xx by default, which would show a success toast
 * over a refusal.
 */

export interface SimulatedOrderFax {
  id: string;
  created_at: string;
  agency_id: string;
  agency_name: string | null;
  clinic_name: string | null;
  /** The agency fax line it would have gone out on. */
  from_fax_number: string | null;
  /** The physician's fax number it was addressed to. */
  to_fax_number: string;
  physician_name: string | null;
  /** Enough to tell two otherwise identical sends apart. */
  patient_label: string | null;
  page_count: number | null;
  /** Set once somebody has signed this one back. Only ever set once. */
  returned_at: string | null;
}

/** What the real inbound pipeline made of the page that was sent back. */
export interface SignedBackOrderFax {
  received_fax_id: string;
  /** True when the mark on the page found the order it answers. */
  matched: boolean;
  message: string;
}

export async function listSimulatedOrderFaxes(): Promise<SimulatedOrderFax[]> {
  const response = await client.get<SimulatedOrderFax[], unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: '/internal-tools/simulated-order-faxes',
  });
  return response.data;
}

/**
 * The exact bytes that would have gone down the line, mark and all.
 *
 * Fetched rather than linked, because the route is behind the session the app
 * already holds and a bare href would arrive unauthenticated.
 */
export async function getSimulatedOrderFaxPdf(faxId: string): Promise<Blob> {
  const response = await client.get<Blob, unknown, true>({
    responseType: 'blob',
    throwOnError: true,
    url: `/internal-tools/simulated-order-faxes/${faxId}/pdf`,
  });
  return response.data instanceof Blob
    ? response.data
    : new Blob([response.data as BlobPart], { type: 'application/pdf' });
}

/**
 * Stamp a signature on the stored page and push it back through the real
 * inbound pipeline.
 *
 * Answers 409 for a fax that has already come back: the return is what moves
 * the order, and letting it happen twice would make the screen a way to
 * manufacture a second signature for the same order.
 */
export async function signAndReturnSimulatedOrderFax(
  faxId: string
): Promise<SignedBackOrderFax> {
  const response = await client.post<SignedBackOrderFax, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/internal-tools/simulated-order-faxes/${faxId}/sign-and-return`,
  });
  return response.data;
}
